import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Moment } from 'moment'

import { DateTimeRangePicker } from '@optios/react-date-time-range-picker'

import { ITargetPeriod } from '../../Client/ITarget'

import { IProps } from './IProps'
import style from './style.scss'
import { periodMapper } from '../utils'

const PeriodDatePicker: FunctionComponent<IProps> = ({
  period,
  startDate,
  onDateUpdate,
}) => {
  const { t } = useTranslation()
  const [selectedDate, setSelectedDate] = useState<Moment>(startDate)
  const [isRangePickerVisible, setIsRangePickerVisible] =
    useState<boolean>(false)
  const [selectedPeriod, setSelectedPeriod] = useState<ITargetPeriod>(period)
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)

  useEffect(() => {
    const newStartDate = selectedDate.clone()
    const newEndDate = selectedDate.clone()

    newStartDate.startOf(periodMapper(selectedPeriod))
    newEndDate.endOf(periodMapper(selectedPeriod))

    if (!isFirstLoad) {
      onDateUpdate(selectedPeriod, newStartDate, newEndDate)
    }
  }, [selectedPeriod, selectedDate])

  const updateRangeBySelectedDate = (
    newSelectedDate: Moment,
    newPeriod: ITargetPeriod,
  ) => {
    setSelectedDate(newSelectedDate)
    setIsRangePickerVisible(false)
    setSelectedPeriod(newPeriod)
    setIsFirstLoad(false)
  }

  const getDateLabel = (labelDate: Moment) => {
    switch (period) {
      case 'weekly':
        return t('Week {{number}} from {{from}} to {{to}}', {
          number: labelDate.format('W'),
          from: labelDate.clone().startOf('isoWeek').format('DD/MM'),
          to: labelDate.clone().endOf('isoWeek').format('DD/MM/YYYY'),
        })
      case 'monthly':
        return labelDate.format('MMMM YYYY')
      case 'yearly':
        return labelDate.format('YYYY')
    }

    return labelDate.format('dddd D MMMM YYYY')
  }

  return (
    <div className={style.toolbar}>
      <div className="segment">
        <button
          className="toolbar-button button-dialog-datepicker button-dialog-datepicker-from"
          onClick={() => {
            setIsRangePickerVisible(!isRangePickerVisible)
          }}>
          <span className={style.long}>{getDateLabel(selectedDate)}</span>
          <span className={style.short}>{getDateLabel(selectedDate)}</span>
        </button>
      </div>
      <div className={style.block}>
        <div className={style.segment}>
          <button
            className={`toolbar-button group-by-button ${
              period === 'daily' ? 'selected' : ''
            }`}
            data-group-by="daily"
            onClick={() => updateRangeBySelectedDate(selectedDate, 'daily')}>
            {t('Daily')}
          </button>
          <button
            className={`toolbar-button group-by-button ${
              period === 'weekly' ? 'selected' : ''
            }`}
            data-group-by="weekly"
            onClick={() => updateRangeBySelectedDate(selectedDate, 'weekly')}>
            {t('Weekly')}
          </button>
          <button
            className={`toolbar-button group-by-button ${
              period === 'monthly' ? 'selected' : ''
            }`}
            data-group-by="monthly"
            onClick={() => updateRangeBySelectedDate(selectedDate, 'monthly')}>
            {t('Monthly')}
          </button>
          <button
            className={`toolbar-button group-by-button ${
              period === 'yearly' ? 'selected' : ''
            }`}
            data-group-by="yearly"
            onClick={() => updateRangeBySelectedDate(selectedDate, 'yearly')}>
            {t('yearly')}
          </button>
        </div>
      </div>
      {isRangePickerVisible && (
        <div className={style.absolute}>
          <div className={style.rangePicker}>
            <DateTimeRangePicker
              range={false}
              date={!!selectedDate}
              onChange={(newDate: Moment) =>
                updateRangeBySelectedDate(newDate, period)
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default PeriodDatePicker
